import { ChatIcon } from "../components/icons";
import { useT } from "../i18n";

/**
 * Lines the reader has asked about stay marked after the chat closes.
 *
 * Two quiet signals rather than a highlight. The reading position already owns
 * the background — sky for the sentence, sage for the word — so a second tint
 * would fight it on exactly the line being listened to. Instead the words get a
 * dotted underline, the typographic sign for "there is a note on this", and the
 * line ends in a small chat mark that reopens the conversation about it.
 *
 * A line is marked when it has a conversation of its own, or it is the line the
 * chat is currently open about — so a long press that was closed without
 * asking still shows where it was. The mark reopens that line's conversation.
 */

/** The underline for words in a marked line. */
export const askedWords =
  "underline decoration-dotted decoration-sage decoration-2 underline-offset-[0.5em]";

/** askedLines is the set of line texts that have, or are open in, a chat. */
export function askedLines(withChats: string[], open: string | null): Set<string> {
  const lines = new Set(withChats);
  if (open) lines.add(open);
  return lines;
}

/** AskedMark is the chat mark at the end of a marked line. */
export default function AskedMark({ onOpen }: { onOpen: () => void }) {
  const t = useT();
  return (
    <button
      type="button"
      data-testid="asked-mark"
      aria-label={t.chat.reopen}
      onClick={onOpen}
      // Sized in em, so it grows with the reader's text size and sits in the
      // line like a character rather than floating over it.
      className="mx-0.5 inline-grid size-[1.3em] place-items-center rounded-full bg-sage-soft align-middle text-sage-deep transition-colors hover:bg-sage hover:text-on-sage"
    >
      <ChatIcon className="size-[0.75em]" />
    </button>
  );
}
